<template>
  <div class="fts-script-search-list">
    <div class="script-search-header">
      <input
        class="fts-form-control"
        placeholder="Type something to search"
        v-model="searchKeyword"
        @keyup.enter="search"
      />
      <fts-btn
        color="primary"
        outline
        text-transform-init
        :disabled="!searchKeyword"
        @click="search"
      >
        Search
      </fts-btn>
    </div>

    <div class="script-search-content" v-if="list && list.length">
      <div
        v-for="(item, index) in list"
        :key="`${item.id}-${index}`"
        class="script-search-item"
        @click="open(item.type, item.key, item.target)"
      >
        <div class="script-search-short-description">
          {{getShortLabel(item)}}
        </div>

        <div class="script-search-info">
          <div class="script-search-info-ref" v-if="item.type === 'trigger'">
            <fts-label-group
              :item="getReference(item.target)"
              v-if="item.target.type === 'standardActivity'"
            />
            <fts-label-group
              :item="getReference(item.target)"
              emoji
              :emoji-size="12"
              v-else-if="item.target.type === 'activity'"
            />
            <fts-label-group
              :item="getReference(item.target)"
              information
              v-else-if="item.target.type === 'information'"
            />
            <fts-state
              :state="getReference(item.target)"
              v-else-if="item.target.type === 'state'"
            />
            <fts-label-group
              :item="{ name: 'Module' }"
              v-else-if="item.target.type === 'vector'"
            />
            <fts-label-group
              :item="{ name: '(Undefined)' }"
              v-else
            />
          </div>

          <div class="script-search-info-target">
            <fts-label-group
              :item="{
                'emoji': item.target.emoji,
                'name': item.target.name
              }"
              emoji
              :emoji-size="14"
              v-if="item.type == 'standardActivity' || item.type == 'activity'"
            />
            <fts-label-group
              :item="{
                'type': item.target.type,
                'name': item.target.name
              }"
              document
              v-else-if="item.type === 'document'"
            />
            <fts-label-group
              :item="{
                'name': item.target.name
              }"
              v-else-if="item.type == 'logicService'"
            />

            <fts-label-group
              :item="{
                'icon': item.icon,
                'name': item.name
              }"
              fontawesome
              v-if="item.type == 'trigger'"
            />
          </div>
        </div>

        <div class="script-search-match-container">
          <div
            v-for="match in item.matches"
            :key="match"
            class="script-search-match"
            v-html="match"
          ></div>
        </div>
      </div>
    </div>

    <div class="script-search-message" v-else-if="list">
      Nothing here. You might want to consider using different keywords, checking for typos, or adjusting your filters.
    </div>

    <div class="script-search-message" v-else>
      Nothing here. Feel free to enter a keyword to start your search.
    </div>

    <fts-activity-design-modal
      :is-capable-write="isCapableOf(SYSTEM_CAPABILITY.Write)"
      :is-capable-workflow="false"
      :is-capable-add-information="isCapableOf(SYSTEM_CAPABILITY.AddInformation)"
      ref="ftsActivityDesignModal"
    />
    <fts-automation-design-modal ref="ftsAutomationDesignModal" />
    <fts-document-design-modal ref="ftsDocumentDesignModal" />
    <fts-logic-service-design-modal ref="ftsLogicServiceDesignModal" />
    <fts-standard-activity-design-modal
      :is-capable-write="isCapableOf(SYSTEM_CAPABILITY.Write)"
      :is-capable-workflow="false"
      :is-capable-add-information="isCapableOf(SYSTEM_CAPABILITY.AddInformation)"
      ref="ftsStandardActivityDesignModal"
    />
  </div>
</template>

<script>
import { store as canvasDesignStore } from '../../store/canvasDesignStore';
import canvasDesign from '../../commons/canvasDesign';
import event from '../event/event';
import { SYSTEM_CAPABILITY, isCapableOf } from '../../commons/flags';

import FTSBtn from '../../elements/FTSBtn';
import FTSLabelGroup from '../../elements/FTSLabelGroup';
import FTSState from '../../elements/FTSState';

import FTSActivityDesignModal from '../FTSActivityDesignModal';
import FTSAutomationDesignModal from '../FTSAutomationDesignModal';
import FTSDocumentDesignModal from '../FTSDocumentDesignModal';
import FTSLogicServiceDesignModal from '../FTSLogicServiceDesignModal';
import FTSStandardActivityDesignModal from '../FTSStandardActivityDesignModal';
import formDesign from '../../commons/formDesign';

export default {
  components: {
    "fts-btn": FTSBtn,
    "fts-label-group": FTSLabelGroup,
    "fts-state": FTSState,
    "fts-activity-design-modal": FTSActivityDesignModal,
    "fts-automation-design-modal": FTSAutomationDesignModal,
    "fts-document-design-modal": FTSDocumentDesignModal,
    "fts-logic-service-design-modal": FTSLogicServiceDesignModal,
    "fts-standard-activity-design-modal": FTSStandardActivityDesignModal
  },
  data: () => ({
    searchKeyword: null,
    list: null
  }),
  methods: {
    isCapableOf(type) {
      return isCapableOf(type, canvasDesignStore.getters.workspaceModules, canvasDesignStore.state.vectorId);
    },
    search() {
      if (!this.searchKeyword) return;

      const replaceHtmlEntities = (text) => text.replaceAll(/</g, '&lt;').replaceAll(/>/g, '&gt;');
      /**
       * @param {Array} list List to filter
       * @param {string} type Type of design
       * @param {string} key Object property (can be nested)
       * @param {string} keyword Filter keyword 
       * @param {?boolean} isMultiplePreviews Return matched previews (default: false)
       * @param {?number} previewLength Matched preview length (default: 50)
       */
      const getMatchedResults = (list, type, key, keyword, isMultiplePreviews = false, previewLength = 50) => {
        let results = [];
        const filteredResults = list.filter(x => {
          const nestedProperties = key.split('.');
          let nestedValue = x;
          for (const prop of nestedProperties) {
            if (!nestedValue || !(prop in nestedValue))
              return false;

            nestedValue = nestedValue[prop];
          }
          return nestedValue && nestedValue.toLowerCase().includes(keyword && keyword.toLowerCase());
        });

        if (filteredResults.length) {
          filteredResults.forEach(x => {
            let item = {
              id: x.id,
              type: type,
              key: key,
              target: {...x}, 
              matches: []
            };

            if (type == "trigger") {
              const { label, icon } = event.getEventInfo(x);
              item.name = label;
              item.icon = icon;
            }

            const nestedProperties = key.split('.');
            let attributeValue = x;
            for (const prop of nestedProperties) {
              if (!attributeValue || !(prop in attributeValue))
                return;

              attributeValue = attributeValue[prop];
            }

            const escapedKeyword = RegExp.escape(keyword);
            const matchesIterator = attributeValue.matchAll(new RegExp(escapedKeyword, 'gi'));
            const matchesArray = Array.from(matchesIterator);
            const matches = isMultiplePreviews ? matchesArray : (matchesArray.length > 0 ? [matchesArray[0]] : []);
            for (const match of matches) {
              const startIndex = Math.max(0, match.index - previewLength);
              const endIndex = Math.min(attributeValue.length, match.index + escapedKeyword.length + previewLength);
              let preview = attributeValue.substring(startIndex, endIndex);
              if (startIndex > 0)
                preview = '...' + preview;

              if (endIndex < attributeValue.length)
                preview = preview + '...';

              preview = replaceHtmlEntities(preview);
              const highlightedPreview = preview.replace(new RegExp(`(${replaceHtmlEntities(escapedKeyword)})`, 'gi'), '<span>$1</span>');
              item.matches.push(highlightedPreview);
            }

            results.push(item);
          });
        }
        return results;
      }

      this.list = [
        // trigger #After Script
        ...getMatchedResults(
          canvasDesignStore.getters.vectorTriggers,
          "trigger",
          "afterScript",
          this.searchKeyword
        ),
        // trigger #Before Script
        ...getMatchedResults(
          canvasDesignStore.getters.vectorTriggers,
          "trigger",
          "beforeScript",
          this.searchKeyword
        ),
        // trigger #Recipient Script
        ...getMatchedResults(
          canvasDesignStore.getters.vectorTriggers,
          "trigger",
          "recipientScript",
          this.searchKeyword
        ),
        // trigger #Condition Script
        ...getMatchedResults(
          canvasDesignStore.getters.vectorTriggers,
          "trigger",
          "conditionScript",
          this.searchKeyword
        ),
        // trigger #Query Script
        ...getMatchedResults(
          canvasDesignStore.getters.vectorTriggers,
          "trigger",
          "queryScript",
          this.searchKeyword
        ),
        // trigger #Message #Subject
        ...getMatchedResults(
          canvasDesignStore.getters.vectorTriggers,
          "trigger",
          "subjectMessage",
          this.searchKeyword
        ),
        // trigger #Message #Message
        ...getMatchedResults(
          canvasDesignStore.getters.vectorTriggers,
          "trigger",
          "messageTemplate",
          this.searchKeyword
        ),
        // trigger #Customize Message #Subject
        ...getMatchedResults(
          canvasDesignStore.getters.vectorTriggers,
          "trigger",
          "subjectTemplate",
          this.searchKeyword
        ),
        // trigger #Customize Message #Content
        ...getMatchedResults(
          canvasDesignStore.getters.vectorTriggers,
          "trigger",
          "template",
          this.searchKeyword
        ),
        // standard activity #Backend Script
        ...getMatchedResults(
        canvasDesignStore.getters.canvasStandardActivities,
          "standardActivity",
          "design.backendScript",
          this.searchKeyword
        ),
        // activity #Backend Script
        ...getMatchedResults(
          canvasDesignStore.getters.canvasActivities,
          "activity",
          "design.backendScript",
          this.searchKeyword
        ),
        // document
        ...getMatchedResults(
          canvasDesignStore.getters.canvasDocuments,
          "document",
          "html",
          this.searchKeyword
        ),
        // logic service #Script
        ...getMatchedResults(
          canvasDesignStore.getters.canvasLogicServices,
          "logicService",
          "script",
          this.searchKeyword
        )
      ];
    },
    reset() {
      this.searchKeyword = null;
      this.list = null;
    },
    async open(type, key, item) {
      if (!(item && item.id)) {
        this.$toasted.global.warn();
      } else if (type == "trigger") {
        const res = await this.$refs.ftsAutomationDesignModal.show(item, { step: key });
        if (res) {
          this.search();
        }
      } else if (type == "standardActivity") {
        let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
        const res = await this.$refs.ftsStandardActivityDesignModal.show(
          item, null, null, { step: 'setting' }
        );
        if (res && res !== true) {
          canvasDesign.setStandard(canvas, res);
          /* add information fields to forms */
          res.formData.design && formDesign.addInformationFieldsToForms(canvas);
          /* compact classification */
          commons.compactClassification(canvas);
          canvasDesignStore.dispatch("updateVectorCanvas", canvas);
          this.search();
        }
      } else if (type == "activity") {
        const res = await this.$refs.ftsActivityDesignModal.show(
          item, null, null, null, { step: 'setting' }
        );

        if (res) {
          let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
          if (res === true) {
            canvasDesign.deleteActivity(canvas, item);
          } else if (res.formData) {
            canvasDesign.setActivity(canvas, res.formData);
            /* add information fields to forms */
            res.formData.design && formDesign.addInformationFieldsToForms(canvas);
            /* compact classification */
            commons.compactClassification(canvas);
          } else {
            return;
          }
          canvasDesignStore.dispatch("updateVectorCanvas", canvas);
          this.search();
        }
      } else if (type == "document") {
        const res = await this.$refs.ftsDocumentDesignModal.show(item);
        if (res) {
          let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
          if (res === true) {
            canvasDesign.deleteDocument(canvas, item);
            canvasDesignStore.dispatch("updateVectorCanvas", canvas);
          this.search();
          } else if (res.formData) {
            canvasDesign.setDocument(canvas, res.formData);
          }
        }
      } else if (type == "logicService") {
        const res = await this.$refs.ftsLogicServiceDesignModal.show(item);
        if (res) {
          if (res === true) {
            canvasDesign.deleteLogic(canvas, item);
          } else {
            canvasDesign.setLogic(canvas, res);
          }
          canvasDesignStore.dispatch("updateVectorCanvas", canvas);
          this.search();
        }
      }
    },
    getShortLabel({ type, key } = {}) {
      if (type == "standardActivity" || type == "activity")
        return "Backend Script";
      else if (type == "document")
        return "HTML";
      else if (type == "logicService")
        return "Logic";
      else if (type == "trigger" && key == "afterScript")
        return "After Script";
      else if (type == "trigger" && key == "beforeScript")
        return "Before Script";
      else if (type == "trigger" && key == "recipientScript")
        return "Recipient Script";
      else if (type == "trigger" && key == "conditionScript")
        return "Condition Script";
      else if (type == "trigger" && key == "queryScript")
        return "Query Script";
      else if (type == "trigger" && key == "subjectMessage")
        return "Message's Subject";
      else if (type == "trigger" && key == "messageTemplate")
        return "Message's Content";
      else if (type == "trigger" && key == "subjectTemplate")
        return "Customize Message's Subject";
      else if (type == "trigger" && key == "template")
        return "Customize Message's Content";
    },
    getReference({ item, type }) {
      if (type == "standardActivity")
        return canvasDesignStore.getters.canvasStandardActivities.find(x => x.id === item);
      else if (type == "activity")
        return canvasDesignStore.getters.canvasActivities.find(x => x.id === item);
      else if (type === "state")
        return canvasDesignStore.getters.canvasStates.find(x => x.id === item);
      else if (type === "information")
        return canvasDesignStore.getters.canvasInformation.find(x => x.id === item);
    }
  },
  created() {
    this.SYSTEM_CAPABILITY = SYSTEM_CAPABILITY;
  }
}
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;

.fts-script-search-list {
  .script-search-header {
    display: flex;
    align-items: center;
    .fts-form-control {
      margin-right: 0.75rem;
      border-radius: 0.25rem;
    }
  }
  .script-search-content {
    margin-top: 1.5rem;
  }
  .script-search-item {
    margin-bottom: 0.75rem;
    padding: 0.75rem;
    border-radius: 0.375rem;
    border: 1px solid var(--fts-border);
    &:hover {
      cursor: pointer;
    }
  }
  .script-search-short-description {
    margin-bottom: 0.25rem;
    font-size: $font-size-sm;
    line-height: $line-height;
    color: var(--fts-default);
  }
  .script-search-info {
    display: flex;
    align-items: center;
    font-size: $font-size;
    line-height: $line-height;
  }
  .script-search-info-ref {
    min-width: 50px;
    max-width: 50%;
    margin-right: 0.75rem;
  }
  .script-search-info-target {
    min-width: 0;
  }
  .script-search-match-container {
    margin-top: 0.375rem;
    font-size: $font-size;
    line-height: $line-height;
  }
  .script-search-match {
    span {
      background-color: rgba(var(--fts-warning-rgb), 0.125);
      color: var(--fts-warning);
    }
  }
  .script-search-message {
    padding-top: 1.5rem;
    padding-bottom: 1rem;
    font-size: $font-size;
    line-height: $line-height;
    text-align: center;
    color: var(--fts-default);
  }
}
</style>